import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useDebugger } from '../../../contexts/DebuggerContext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * A stylized 404 page using MUI theme system for the SASS product
 */
export interface Saas404Props {
  // In the future this target for the 404 may influence the theme
  target: 'AUTH' | 'LANDING';
}

const Saas404: React.FC<Saas404Props> = ({ target }) => {
  const logger = useDebugger();

  logger.log('Saas404', `404 for ${target}`);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.secondary',
        padding: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: 6,
            textAlign: 'center',
            borderRadius: 2,
            background: 'background.paper',
            border: '2px solid',
            borderColor: 'grey.100',
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'error.main',
                boxShadow: 3,
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 64,
                  color: 'background.paper',
                }}
              />
            </Box>
          </Box>

          {/* 404 Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: '6rem',
              fontWeight: 700,
              color: 'primary.main',
              marginBottom: 2,
              lineHeight: 1,
            }}
          >
            404
          </Typography>

          {/* Error Title */}
          <Typography
            variant="h4"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {target === 'AUTH' ? 'Authentication Required' : 'Page Not Found'}
          </Typography>

          {/* Error Description */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: '400px',
              margin: '0 auto',
              marginBottom: 4,
            }}
          >
            {target === 'AUTH'
              ? 'Unable to access this page. Please ensure you have a valid tenant name in the URL or sign in with appropriate credentials.'
              : 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.'}
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                paddingX: 3,
                paddingY: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Go to Home
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{
                paddingX: 3,
                paddingY: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  borderColor: 'secondary.dark',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Refresh Page
            </Button>
          </Box>

          {/* Additional Help Text */}
          <Box
            sx={{
              paddingTop: 3,
              borderTop: '1px solid',
              borderTopColor: 'grey.200',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              {target === 'AUTH'
                ? 'If you believe this is an error, please contact your administrator.'
                : 'Please check the URL or use the navigation above to find what you need.'}
            </Typography>
          </Box>
        </Paper>

        {/* Debug Information (only shown in development) */}
        {process.env.NODE_ENV === 'development' && (
          <Box
            sx={{
              marginTop: 2,
              padding: 2,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
              }}
            >
              Debug: 404 triggered for {target} context
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Saas404;
